import { H2 } from "@/components/ui/Heading";
import { PropertyListingCategory } from "@/contexts/SearchContext/enums";
import { Locale } from "@/enums/common.enum";
import { BoostType } from "@/enums/property.enum";
import { Link } from "@/i18n/routing";
import { Property } from "@/interfaces/property.interface";
import { gTagEvents } from "@/lib/gtag";
import { propertiesToViewItemECommerce } from "@/lib/gtag/helpers";
import { useLocale } from "next-intl";
import Image from "next/image";
import React, { useCallback, useEffect } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import { ContentSection } from "../PropertyViewCard/ContentSection";
import { PropertyCaseFooter } from "./PropertyCaseFooter";
import { PropertyCaseHeader } from "./PropertyCaseHeader";

export interface PropertyShowCaseCardProps {
  variant?: BoostType;
  slotProps: {
    link: React.ComponentProps<typeof Link>;
  };
  property: Property;
}

export const PropertyShowCaseCard = (props: PropertyShowCaseCardProps) => {
  const { slotProps, property } = props;

  const locale = useLocale() as Locale;
  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 0.9,
    freezeOnceVisible: true,
  });

  const title = property.description[locale].title;

  useEffect(() => {
    if (isIntersecting) {
      gTagEvents.viewPromotion({
        creative_name: title,
        creative_slot: "1",
        promotion_id: property.id,
        promotion_name: PropertyListingCategory.PROPERTY_SHOWCASE,
        ...propertiesToViewItemECommerce(
          [property],
          PropertyListingCategory.PROPERTY_SHOWCASE
        ),
      });
    }
  }, [isIntersecting]);

  const handleClick = useCallback(() => {
    gTagEvents.selectPromotion({
      creative_name: title,
      creative_slot: "1",
      promotion_id: property.id,
      promotion_name: PropertyListingCategory.PROPERTY_SHOWCASE,
      ...propertiesToViewItemECommerce(
        [property],
        PropertyListingCategory.PROPERTY_SHOWCASE
      ),
    });
  }, [property]);

  return (
    <Link {...slotProps.link} ref={ref} onClick={() => handleClick()}>
      <div className=" rounded-xl overflow-hidden shadow-sm border ">
        <PropertyCaseHeader property={property} />

        <div className="relative overflow-hidden">
          {property?.photos[0] && (
            <div className="relative w-full overflow-hidden h-[200px]">
              <Image
                src={property?.photos[0]}
                fill
                unoptimized={property?.photos[0].startsWith("https")}
                alt={`${title}-1`}
                className=" w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 768px) 0vw, 20vw"
                priority
                fetchPriority="high"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className=" absolute top-0 bottom-0 right-0 left-0 flex justify-start items-end ">
                <div className=" text-md font-extrabold  text-white  px-3 py-2 ">
                  {title}
                </div>
              </div>
            </div>
          )}

          <div className=" flex border-t-2 border-white divide-x-2 ">
            {property.photos.slice(1, 3).map((image, index) => (
              <div
                key={index}
                className="relative w-full overflow-hidden h-[110px]"
              >
                <Image
                  src={image}
                  unoptimized={image.startsWith("https")}
                  fill
                  alt={`${title}-${index + 1}`}
                  className=" w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  sizes="(max-width: 768px) 0vw, 10vw"
                  priority
                  fetchPriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
            ))}
          </div>
        </div>
        <ContentSection
          listingId="property_showcase"
          property={property}
          containerClassName="p-3"
          cardType="showcase"
        />
        <H2 className="hidden"> {title}</H2>
        <PropertyCaseFooter {...{ property }} />
      </div>
    </Link>
  );
};
